import type {
  PurchaseOfferState,
  PurchaseOfferTaskGroupPhase,
} from 'components/buyer/__generated__/purchase-offers.zeus';

type PurchaseOfferStateCategory =
  | 'CANCELED'
  | 'CLOSED'
  | 'CONTRACT_PENDING'
  | 'IN_CONTRACT'
  | 'OFFER_REVIEW'
  | 'OTHER_ACTIVE';

const PURCHASE_OFFER_STATE_CATEGORY = {
  ACTIVE: 'OFFER_REVIEW',
  ATTORNEY_REVIEW: 'CONTRACT_PENDING',
  AWAITING_CUSTOMER_SIGNATURE: 'CONTRACT_PENDING',
  BACKUP_EXPIRED: 'CANCELED',
  BACKUP_OFFER: 'OTHER_ACTIVE',
  BUYER_WALKED: 'CANCELED',
  CANCELED: 'CANCELED',
  CLOSED: 'CLOSED',
  CONTINGENCIES_RELEASED: 'IN_CONTRACT',
  CONTRACT_FELL_THROUGH: 'CANCELED',
  COUNTER_OFFER_PENDING: 'OTHER_ACTIVE',
  IN_CONTRACT: 'IN_CONTRACT',
  IN_NEGOTIATION: 'OTHER_ACTIVE',
  REJECTED: 'CANCELED',
  UNKNOWN: 'CANCELED',
} as const satisfies Record<PurchaseOfferState, PurchaseOfferStateCategory>;

const offerStateCategory = (
  state: PurchaseOfferState | null | undefined,
): PurchaseOfferStateCategory | undefined =>
  state ? PURCHASE_OFFER_STATE_CATEGORY[state] : undefined;

export const isOfferCanceled = (state: PurchaseOfferState | null | undefined): boolean =>
  offerStateCategory(state) === 'CANCELED';

export const isActiveBuyerOffer = (state: PurchaseOfferState | null | undefined): boolean => {
  const category = offerStateCategory(state);
  return category !== undefined && category !== 'CANCELED' && category !== 'CLOSED';
};

export const isInContract = (state: PurchaseOfferState | null | undefined): boolean =>
  offerStateCategory(state) === 'IN_CONTRACT';

export const isInContractOrClosed = (state: PurchaseOfferState | null | undefined): boolean => {
  const category = offerStateCategory(state);
  return category === 'IN_CONTRACT' || category === 'CLOSED';
};

export const isInOfferReview = (state: PurchaseOfferState | null | undefined): boolean => {
  const category = offerStateCategory(state);
  return category === 'OFFER_REVIEW' || category === 'CONTRACT_PENDING';
};

export const isContractPending = (state: PurchaseOfferState | null | undefined): boolean =>
  offerStateCategory(state) === 'CONTRACT_PENDING';

export interface TaskGroupAvailability {
  disabled: boolean;
  reason?: string;
}

/**
 * Whether a purchase task group is actionable for the given transaction state.
 * Some groups (welcome-home, escrow & closing) are surfaced by the API before
 * the buyer can act on them, so callers that count or advertise tasks must gate
 * on this rather than treating every returned group as available.
 */
export const getTaskGroupDisabledState = (
  phase: PurchaseOfferTaskGroupPhase,
  transactionState: PurchaseOfferState | null | undefined,
): TaskGroupAvailability => {
  // All task groups are disabled when the offer is canceled.
  if (isOfferCanceled(transactionState)) {
    return { disabled: true, reason: 'Offer is no longer active' };
  }

  switch (phase) {
    case 'WELCOME_HOME':
      if (transactionState === 'CLOSED') {
        return { disabled: false };
      }
      return { disabled: true, reason: 'Available after closing' };
    case 'ESCROW_CLOSING':
      if (isInContractOrClosed(transactionState)) {
        return { disabled: false };
      }
      return { disabled: true, reason: 'Available when in contract' };
    case 'OFFER':
      return { disabled: false };
  }

  phase satisfies never;
  return { disabled: false };
};
